import { object } from '@art-suite/art-core-ts';
import { AllParseAssets, ParsedExpression } from './InternalTypes';
import { ExpressionError, ExpressionMap, ValueOrExpression } from './OptimizedValueExpressionTypes';
import { getCachedParsedExpression } from './ParserCache';
import { standardFunctions } from './StandardFunctions';

export const expressionMapValueIsEquation = (value: ValueOrExpression | undefined): value is string => typeof value === 'string';
export const expressionMapValueIsNumber = (value: ValueOrExpression | undefined): value is number => typeof value === 'number';
export const expressionMapValueIsNumberOrUndefined = (value: ValueOrExpression | undefined): value is number | undefined =>
  expressionMapValueIsNumber(value) || value === undefined;
export const getExpressionMapNumbers = (expressionMap: ExpressionMap): Record<string, number | undefined> =>
  object(expressionMap as any, { when: expressionMapValueIsNumberOrUndefined }) as any;

/**
 * Get the evaluation order of the expressions
 * @param dependencyGraph - The dependency graph of the expressions
 * @returns The evaluation order of the expressions
 */
export const getEvaluationOrder = (dependencyGraph: Record<string, string[]>): string[] => {
  const order: string[] = [];
  const resolving: string[] = [];

  const recursiveResolve = (name: string) => {
    if (!dependencyGraph[name]) return; // ignore external dependencies
    if (resolving.includes(name)) throw new Error(`Circular dependency detected in expressions: ${resolving.join(' -> ')} -> ${name}`);
    if (order.includes(name)) return; // already resolved
    resolving.push(name);
    dependencyGraph[name].forEach(recursiveResolve);
    order.push(name);
    resolving.pop();
  };

  for (const key in dependencyGraph) {
    recursiveResolve(key);
  }
  return order;
};

export const getAllParseAssetsForExpressions = (expressions: Record<string, string>): AllParseAssets => {
  let errors: ExpressionError[] | undefined = undefined;

  // parse the expressions
  const parsedExpressionsByName: Record<string, ParsedExpression> = {};
  for (const name in expressions) {
    const expression = expressions[name];
    try {
      parsedExpressionsByName[name] = { name, ...getCachedParsedExpression(expression) };
    } catch (error: any) {
      if (!errors) errors = [];
      errors.push({ name, error: `Invalid expression: ${JSON.stringify(expression)} (name: ${name})` });
    }
  }

  // validate that all functions are supported
  const supportedFunctions = Object.keys(standardFunctions);
  for (const name in parsedExpressionsByName) {
    const parsedExpression = parsedExpressionsByName[name];
    const unsupportedFunctions = parsedExpression.functions.filter(func => !supportedFunctions.includes(func));
    if (unsupportedFunctions.length > 0) {
      if (!errors) errors = [];
      errors.push({
        name,
        error: `Unsupported function${unsupportedFunctions.length > 1 ? 's' : ''} in expression "${parsedExpression.expression}": ${
          unsupportedFunctions.join(', ')
        }. Supported functions: ${supportedFunctions.join(', ')}`,
      });
    }
  }

  // build the dependency graph
  const dependencyGraph: Record<string, string[]> = {};
  for (const name in parsedExpressionsByName) {
    dependencyGraph[name] = parsedExpressionsByName[name].dependencies;
  }

  // get the evaluation order
  const evaluationOrder = getEvaluationOrder(dependencyGraph);

  // order the parsed expressions
  const orderedParsedExpressions: ParsedExpression[] = [];
  for (const name of evaluationOrder) {
    orderedParsedExpressions.push(parsedExpressionsByName[name]);
  }

  return { parsedExpressionsByName, dependencyGraph, evaluationOrder, orderedParsedExpressions, errors };
};
